import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    CommandLineIcon,
    ExclamationCircleIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import i18n from '../i18n';

const API_URL = '/api/v1';

const LogDetailModal = ({ log, onClose }) => {
    if (!log) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm animate-fade-in">
            <div className="glass-card max-w-4xl w-full p-8 rounded-none shadow-2xl border border-gray-200 animate-scale-in max-h-[80vh] overflow-y-auto bg-white">
                <div className="flex justify-between items-start mb-8">
                    <div className="flex gap-4 items-center">
                        <div className="w-16 h-16 bg-blue-50 rounded flex items-center justify-center text-[#155e75]">
                            <CommandLineIcon className="w-10 h-10" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800 uppercase tracking-tight">{log.action}</h2>
                            <p className="text-gray-400 font-bold text-sm tracking-tight">{log.timestamp}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 text-2xl font-light">&times;</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-6">
                    <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">{i18n.t('audit_old_state')}</p>
                        <pre className="p-4 bg-gray-50 rounded border border-gray-100 text-[10px] text-red-600 font-mono overflow-x-auto">
                            {JSON.stringify(log.old_values, null, 2)}
                        </pre>
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-4">{i18n.t('audit_new_state')}</p>
                        <pre className="p-4 bg-emerald-50 rounded border border-emerald-100 text-[10px] text-emerald-700 font-mono overflow-x-auto">
                            {JSON.stringify(log.new_values, null, 2)}
                        </pre>
                    </div>
                </div>

                <div className="mt-8 flex justify-end">
                    <button onClick={onClose} className="px-8 py-3 rounded-sm bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 transition-all text-xs uppercase tracking-widest">{i18n.t('close_entry')}</button>
                </div>
            </div>
        </div>
    );
};

export default function AuditLog() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedLog, setSelectedLog] = useState(null);

    const fetchLogs = async () => {
        try {
            const response = await axios.get(`${API_URL}/audit`);
            setLogs(response.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching logs:', err);
            setError(i18n.t('audit_error'));
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    if (loading) return (
        <div className="py-20 text-center space-y-4">
            <div className="w-10 h-10 border-4 border-[#155e75] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-gray-400 font-mono text-xs font-bold uppercase tracking-widest">{i18n.t('sov_node')}</p>
        </div>
    );

    return (
        <div className="space-y-8 animate-fade-in">
            {selectedLog && <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}

            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">{i18n.t('audit_trail')}</h1>
                </div>
            </div>

            {error && (
                <div className="p-4 rounded-sm bg-red-50 border border-red-100 text-red-600 flex items-center gap-3">
                    <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                    <p className="text-xs font-bold">{error}</p>
                </div>
            )}

            <div className="glass-card rounded-none overflow-hidden border-gray-200 font-mono shadow-sm bg-white">
                <table className="ent-table">
                    <thead>
                        <tr>
                            <th>{i18n.t('audit_action')}</th>
                            <th>{i18n.t('audit_entity')}</th>
                            <th>{i18n.t('audit_actor')}</th>
                            <th>{i18n.t('audit_timestamp')}</th>
                            <th className="text-right">{i18n.t('audit_details')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log) => (
                            <tr key={log.id} className="hover:bg-blue-50/30 transition-colors">
                                <td>
                                    <span className={`badge ${log.action.includes('ISSUE') ? 'badge-success' :
                                        log.action.includes('TRANSFER') ? 'badge-info' :
                                            'badge-warning'
                                        }`}>
                                        {log.action}
                                    </span>
                                </td>
                                <td className="font-bold text-gray-500 text-[10px]" title={log.entity_type}>{log.entity_id.substring(0, 12)}...</td>
                                <td className="text-gray-500 font-bold text-[10px] truncate max-w-[100px]">{log.actor_id.split('@')[0]}</td>
                                <td className="text-gray-400 text-[10px] tabular-nums">{log.timestamp}</td>
                                <td className="text-right">
                                    <button
                                        onClick={() => setSelectedLog(log)}
                                        className="p-1.5 rounded bg-gray-50 text-gray-400 hover:text-[#155e75] hover:bg-blue-50 transition-all"
                                    >
                                        <EyeIcon className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {logs.length === 0 && (
                    <div className="px-8 py-20 text-center text-gray-400 text-xs italic uppercase tracking-widest font-bold">
                        {i18n.t('audit_empty')}
                    </div>
                )}
            </div>

            <div className="p-8 rounded-none bg-emerald-50 border border-emerald-100 flex gap-6">
                <div className="w-12 h-12 bg-white rounded border border-emerald-100 flex items-center justify-center shrink-0 shadow-sm text-emerald-600">
                    <CommandLineIcon className="w-6 h-6" />
                </div>
                <div className="text-xs">
                    <p className="text-emerald-700 font-black mb-2 uppercase tracking-widest">{i18n.t('audit_standard')}</p>
                    <p className="text-gray-500 leading-relaxed max-w-4xl">
                        {i18n.t('audit_desc')}
                    </p>
                </div>
            </div>
        </div>
    );
}
